import {action, computed, observable, runInAction} from "mobx";

import {TemplateItemDetailOutput} from "../../../api/values/template-item-detail-output";
import {Permission} from "../../../stores/authentication-store";
import {DatabaseRecordBasePageStore} from "../../../stores/database-template/database-record-base-store";

// 新規作成と編集ページで共通のロジックを持つベースストアを継承
export class DatabaseTemplateRecordEditPageStore extends DatabaseRecordBasePageStore {
    @observable public recordId = "";
    @observable public itemId = "";
    @observable public _originalDetailColumn: TemplateItemDetailOutput =
        new TemplateItemDetailOutput();

    public async initialize({
        query,
    }: {
        query: {mediaId: string; templateId: string; itemId: string};
    }) {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_SYSTEM,
            Permission.MANAGE_MEDIA,
        );

        this.setMediaId(query.mediaId);
        this.setTemplateId(query.templateId);
        this.setItemId(query.itemId);
        await this.loadTemplate();
        await this.loadRecord();
    }
    @action
    public setItemId(itemId: string) {
        this.itemId = itemId;
    }
    private async loadRecord() {
        try {
            const res = await this.adminApi.getTemplateItemDetail(
                {mediaId: this.mediaId},
                {templateId: this.templateId, itemId: this.itemId},
            );

            runInAction(() => {
                this.templateDataLabel = res.itemLabel;
                // まず値とオプションをセット
                res.itemList.forEach((item) => {
                    const row = this.templateColumnList.find((r) => r.slug === item.slug);
                    if (!row) return;
                    this.values[row.id] = item.value;
                    this.selectedOptions[row.id] = {
                        imageUrl: item.options.link_url ?? "",
                        altText: item.options.alt_text ?? "",
                        sourceTitle: item.options.source_text ?? "",
                        sourceUrl: item.options.source_url ?? "",
                    };
                });

                // deleted=true の slug を取得
                const deletedSlugs = this.templateColumnList
                    .filter((r) => r.deleted)
                    .map((r) => r.slug);
                // deletedSlug と一致する slug を削除
                this.templateColumnList = this.templateColumnList.filter(
                    (r) => !deletedSlugs.includes(r.slug),
                );
                this._originalDetailColumn = res;
            });
        } catch (error) {
            throw new Error("Error loading template item: " + error);
        }
    }
    @computed
    public get canUpdate(): boolean {
        const trimmedLabel = this.templateDataLabel.trim();
        if (trimmedLabel.length === 0 || this.templateDataLabelError) return false;

        const originalLabel = (this._originalDetailColumn.itemLabel ?? "").trim();
        if (originalLabel !== trimmedLabel) {
            return true;
        }

        // オリジナルの Map を作成
        const originalMap = new Map<string, {value: string; options: Record<string, string>}>();
        this._originalDetailColumn.itemList.forEach((col) => {
            originalMap.set(col.slug, {
                value: col.value,
                options: {
                    imageUrl: col.options.link_url ?? "",
                    altText: col.options.alt_text ?? "",
                    sourceTitle: col.options.source_text ?? "",
                    sourceUrl: col.options.source_url ?? "",
                },
            });
        });
        // いずれかのカラムで差分があれば true
        return this.templateColumnList.some((row) => {
            const orig = originalMap.get(row.slug);
            if (!orig) return true;
            const currentValue = this.values[row.id] ?? "";
            if (orig.value !== currentValue) return true;
            const currentOpts = this.selectedOptions[row.id] || {};
            return (
                orig.options.imageUrl !== (currentOpts.imageUrl || "") ||
                orig.options.altText !== (currentOpts.altText || "") ||
                orig.options.sourceTitle !== (currentOpts.sourceTitle || "") ||
                orig.options.sourceUrl !== (currentOpts.sourceUrl || "")
            );
        });
    }

    @action.bound
    public async updateTemplateRecord() {
        const dto = this.buildUpsertDto();
        try {
            await this.adminApi.putTemplateItemDetail(
                {mediaId: this.mediaId},
                {templateId: this.templateId, itemId: this.itemId},
                dto,
            );
        } catch (error) {
            console.error(error);
        }
        this.pageRouter.pushRoute(
            `/admin/${this.mediaId}/database-templates/detail/${this.templateId}?tab=management`,
        );
    }
}
